import { useHistory } from 'react-router';
import { SequenceDetailsDocument, SequenceDetailsQuery } from '~/data';
import { client } from '~/data/client';
import { TrainingState, useTrainingStore } from './state';
import { useTrainingParams } from './useTrainingParams';
import { useTrainingUrl } from './useTrainingUrl';
import { isIOS, isIOS13, isIPad13, isIPhone13 } from 'react-device-detect';

const selector = (state: TrainingState) => state.next;

/**
 * iOS moves between sequences with a full page load, which keeps the memory
 * footprint of a long training in check. The reload also throws away the audio
 * element along with the autoplay permission WebKit granted it on the last tap,
 * so a sequence whose narration is supposed to start on its own has to be
 * reached without leaving the document.
 *
 * Only the prefetched cache is consulted (see `usePrefetchSequence`): a miss
 * keeps the reload, trading the occasional silent narration for the memory
 * behaviour we have relied on since 2022.
 */
function narrationWillAutoplay(
  learnerTrainingUuid: string,
  sequenceId: string
) {
  const sequence = useTrainingStore
    .getState()
    .items.find(i => i.kind === 'sequence' && i.id === sequenceId);

  if (!sequence || sequence.kind !== 'sequence') return false;

  try {
    const cached = client.readQuery<SequenceDetailsQuery>({
      query: SequenceDetailsDocument,
      variables: {
        learnerTrainingUuid,
        chapterSequenceId: sequence.chapterSequenceId
      }
    });

    return Boolean(cached?.sequence?.narration && cached.sequence.playAudio);
  } catch (error) {
    return false;
  }
}

export function useNextSequence() {
  const url = useTrainingUrl();
  const history = useHistory();
  const next = useTrainingStore(selector);
  const { learnerTrainingId } = useTrainingParams();

  return async () => {
    const id = await next();

    const isIOSDevice = isIOS || isIOS13 || isIPad13 || isIPhone13;

    if (isIOSDevice && !narrationWillAutoplay(learnerTrainingId ?? '', id)) {
      window.location.href = `${url}/${id}`;
    } else {
      history.push(`${url}/${id}`);
    }
  };
}
